import defaultAuthor from "@/constants/defaultAuthor";
import siteInfo from "@/constants/siteInfo";
import dayjs from "dayjs";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import SVG from "react-inlinesvg";

import { defaultAuthorMap } from "@/utils/langCodeMapper";
import sendGAEvent from "@/utils/gaEvent";

const LivePlayerCard = dynamic(() => import("@/component/LivePlayerCard"));

const getAuthorDetails = (content) => {
  const defaultAuthorState =
    content?.langcode &&
    content?.langcode in defaultAuthorMap &&
    defaultAuthorMap[content?.langcode];

  return {
    authorLink: content?.author?.[0]?.canonical_url
      ? content.author[0].canonical_url
      : defaultAuthorState
      ? defaultAuthorState.link
      : defaultAuthor.canonical_url,
    authorTitle:
      (content?.author?.[0]?.title ||
        (content?.author_detail?.[0]?.title !== "mediology"
          ? content?.author_detail?.[0]?.title
          : defaultAuthorState?.name) ||
        defaultAuthorState?.name) ?? defaultAuthor.title,
  };
};

const getImageUrl = (content, size) => {
  const image = content?.image_small || content?.thumb_image;

  if (!image) {
    return null;
  }

  return `${image}${image.includes("?") ? "&" : "?"}size=${size}`;
};

const MetaLine = ({ content, authorLink, authorTitle, hideWatchIcon = false }) => (
  <p className="text-xs text-gray-500 font-extralight mt-1 min-h-[36px] sm:min-h-[20px]">
    {(content?.content_type !== "video" || !hideWatchIcon) && (
      <img
        className="h-[16px] w-[16px] inline-block mr-1 align-text-bottom"
        alt="read video icon"
        loading="lazy"
        height={16}
        width={16}
        src={
          content?.content_type === "video"
            ? "https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/watchType.svg"
            : "https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/readType.svg"
        }
      />
    )}
    <time dateTime={content?.datetime_published || content?.datetime}>
      {dayjs(content?.datetime_published || content?.datetime).format("MMM DD YYYY")}
    </time>{" "}
    · By{" "}
    <a href={authorLink} target={content?.publishtime ? "_blank" : "_self"}>
      <span className="text-primary">{authorTitle}</span>
    </a>
  </p>
);

const MediaThumb = ({ content, href, size, widthClass, heightClass, iconClass, eager = false }) => {
  const imageUrl = getImageUrl(content, size);

  return (
    <div className={`relative overflow-hidden rounded-md bg-white-200 shrink-0`}>
      <a
        href={href}
        target={content?.publishtime ? "_blank" : "_self"}
        className="block h-full w-full"
      >
        {imageUrl ? (
          <img
            className={`block h-full w-full object-cover  ${widthClass} ${heightClass}`}
            src={imageUrl}
            title={content?.image_title}
            alt={content?.image_title || siteInfo?.siteName}
            fetchpriority={eager ? "high" : "auto"}
            loading={eager ? "eager" : "lazy"}
            role="button"
            width={eager ? 360 : 150}
            height={eager ? 203 : 84}
          />
        ) : (
          <div className="h-full w-full" />
        )}
      </a>
      {content?.content_type === "video" && (
        <a href={href} target={content?.publishtime ? "_blank" : "_self"}>
          <div className="absolute inset-0 flex justify-center items-center">
            <SVG
              className={iconClass}
              src="https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/play-media.svg"
            />
          </div>
        </a>
      )}
    </div>
  );
};

const FirstCard = ({ content, categoryTitle = "", isResultsPage = false }) => {
  const { authorLink, authorTitle } = getAuthorDetails(content);
  const href = content?.canonical_url ?? "";

  return (
    <article className="flex flex-col overflow-hidden min-h-full">
      <MediaThumb
        content={content}
        href={href}
        size="360:203"
        widthClass="w-full"
        heightClass="aspect-[16/9]"
        iconClass="w-10 h-10"
        eager
      />

      <div className="  flex flex-col flex-1">
        <a
          href={href}
          target={content?.publishtime ? "_blank" : "_self"}
          onClick={() => {
            if (!isResultsPage) {
              sendGAEvent({
                eventName: `Home Screen - ${categoryTitle}`,
                pageTitle: document?.title,
                pageLocation: window?.location?.href,
              });
            }
          }}
          className="block min-h-[84px]"
        >
          <h3
            className="text-lg font-extrabold line-clamp-3 hover:text-primary"
            dangerouslySetInnerHTML={{
              __html: `${content?.title_short || content?.title}`,
            }}
          />
        </a>
        <MetaLine content={content} authorLink={authorLink} authorTitle={authorTitle} />
      </div>
    </article>
  );
};

const StandardCard = ({ content, isVideoCard = false, categoryTitle = "", isResultsPage = false }) => {
  const router = useRouter();
  const isHomeVideoCard = router?.asPath === "/" && isVideoCard;
  const { authorLink, authorTitle } = getAuthorDetails(content);
  const href = content?.canonical_url ?? "";

  return (
    <article className="flex rounded-sm overflow-hidden items-start min-h-[96px]">
      <MediaThumb
        content={content}
        href={href}
        size="150:84"
        widthClass="w-[114px] min-w-[114px]"
        heightClass="aspect-[16/9]"
        iconClass="w-8 h-8"
      />

      <div className="pl-2 flex-1 min-h-[96px]">
        <a
          href={href}
          target={content?.publishtime ? "_blank" : "_self"}
          onClick={() => {
            if (!isResultsPage) {
              sendGAEvent({
                eventName: `Home Screen - ${categoryTitle}`,
                pageTitle: document?.title,
                pageLocation: window?.location?.href,
              });
            }
          }}
          className="block min-h-[58px]"
        >
          <h3
            className="text-md font-bold hover:text-primary line-clamp-3"
            dangerouslySetInnerHTML={{
              __html: `${content?.title_short || content?.title}`,
            }}
          />
        </a>
        <MetaLine
          content={content}
          authorLink={authorLink}
          authorTitle={authorTitle}
          hideWatchIcon={isHomeVideoCard}
        />
      </div>
    </article>
  );
};

const NewsGrid = ({
  newsData = null,
  showHeader = true,
  liveVideoData = null,
  isMobileDevice = false,
  showLiveVideo = false,
  isAutoLiveBlog = false,
  isResultsPage = false,
}) => {
  const categoryTitle =
    newsData?.info?.name ?? (isAutoLiveBlog ? "रिजल्ट लाईव् अपडेट्स" : "न्यूज़");

  return (
    <div className="my-2">
      {showHeader && (
        <div className="flex my-2 items-center min-h-[32px]">
          <div className="text-primary font-bold text-lg sm:text-xl xs:text-xl md:text-xl">
            {!isResultsPage && <h2>{newsData?.info?.name ?? "रिजल्ट लाईव् अपडेट्स"}</h2>}
          </div>
          <div className="text-slate-500 font-normal text-sm ml-auto shrink-0">
            {(newsData?.info?.canonical_url || isAutoLiveBlog) && (
              <a
                href={
                  isAutoLiveBlog
                    ? "/election/delhi-assembly/constituency-list#election-auto-live-blogs"
                    : newsData?.info?.canonical_url
                }
                target={newsData?.publishtime ? "_blank" : "_self"}
                onClick={() => {
                  if (!isResultsPage) {
                    sendGAEvent({
                      eventName: `Home Screen - ${newsData?.info?.name ?? "न्यूज़"} View More`,
                      pageTitle: document?.title,
                      pageLocation: window?.location?.href,
                    });
                  }
                }}
              >
                <span className="flex items-center whitespace-nowrap">
                   और देखें‚
                  <SVG
                    className="w-4 h-4 inline ml-2 mt-[-2px]"
                    src="https://akm-img-a-in.tosshub.com/lingo/newstak/resources/prod/rightArrowRound.svg"
                  />
                </span>
              </a>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-2 md:items-start">
        <div className="w-full md:w-1/2">
          {newsData?.content?.[0] && (
            <FirstCard
              content={newsData.content[0]}
              categoryTitle={categoryTitle}
              isResultsPage={isResultsPage}
            />
          )}

          {newsData?.content?.[1] && (
            <div  >
              <StandardCard
                content={newsData.content[1]}
                isVideoCard={newsData?.info?.canonical_url === "/videos"}
                categoryTitle={newsData?.info?.name ?? "न्यूज़"}
                isResultsPage={isResultsPage}
              />
              {showLiveVideo && liveVideoData && isMobileDevice && liveVideoData?.live_id && (
                <div className="my-1 min-h-[240px]">
                  <LivePlayerCard height={240} liveData={liveVideoData} />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="w-full md:w-1/2 md:overflow-auto">
          {newsData?.content?.slice(2)?.map((content, index) => (
            <div key={content?.nid || content?.canonical_url || index} className={` ${isAutoLiveBlog && index > 3 ? "hidden" : ""}`}>
              <StandardCard
                content={content}
                isVideoCard={newsData?.info?.canonical_url === "/videos"}
                categoryTitle={newsData?.info?.name ?? "न्यूज़"}
                isResultsPage={isResultsPage}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsGrid;
